import { calculateHandicap } from "../../shared/calculations";
import {
  DOUBLES_SPLIT,
  ENCODING_LETTER_NUMBER,
  encodingForBowlerIndex,
  formatPair,
  movementSummary,
  pairStartFromLane,
  physicalLane,
} from "../laneAssignment";
import { optionalEventLabel } from "../signupSheets";
import { sortTeamAssignments } from "../teamAssignments";

export const LAYOUT_ONE = "one";
export const LAYOUT_PACK = "pack";

/** Keep in sync with supabase/functions/_shared/recapSheetPdf.ts */
export const RECAP_LAYOUT = {
  PAGE_W: 792,
  PAGE_H: 612,
  MARGIN: 28,
  TITLE_BAND: 36,
  FOOTER: 22,
  PAIR_HEAD: 12,
  TABLE_HEAD: 18,
  ROW_H: 26,
  TOTALS_H: 20,
  PAIR_GAP: 6,
  SHIFT_GAP: 2,
  MAX_GAMES: 6,
};

const slugPart = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);

export const recapSheetFileName = ({ tournamentName, squadDate } = {}) => {
  const tournament = slugPart(tournamentName) || "tournament";
  const squad = slugPart(squadDate) || "squad";
  return `${tournament}-${squad}-recaps.pdf`;
};

export const RECAP_SHEET_PREFIX = "R1";

export const recapTeamShortCode = (teamId) =>
  String(teamId || "")
    .replace(/-/g, "")
    .slice(0, 8)
    .toUpperCase();

export const recapTeamIdsOnPair = (pair) => {
  const ids = [];
  const seen = new Set();
  (pair?.shifts || []).forEach((shift) => {
    const id = String(shift?.teamId || "").trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  });
  return ids;
};

const isTeamShortCode = (value) => /^[A-F0-9]{4,8}$/.test(value);

export const recapSheetPayload = ({ teamIds } = {}) => {
  const codes = (teamIds || [])
    .map((id) => recapTeamShortCode(id))
    .filter(isTeamShortCode);
  if (!codes.length) return "";
  return `${RECAP_SHEET_PREFIX}/${codes.join("+")}`;
};

export const parseRecapSheetPayload = (value) => {
  const text = String(value || "").trim().toUpperCase();
  if (text.startsWith(`${RECAP_SHEET_PREFIX}/`)) {
    const teamCodes = text
      .slice(RECAP_SHEET_PREFIX.length + 1)
      .split("+")
      .map((code) => code.trim())
      .filter(isTeamShortCode);
    if (!teamCodes.length) return null;
    return { teamCodes };
  }
  const parts = text.split("/");
  if (parts.length >= 4 && parts[0] === "BTA.R.1") {
    const tournamentId = parts[1];
    const squadId = parts[2];
    const teamIds = parts
      .slice(3)
      .join("/")
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);
    const teamCodes = teamIds.map(recapTeamShortCode).filter(isTeamShortCode);
    if (!tournamentId || !squadId || !teamCodes.length) return null;
    return { tournamentId, squadId, teamIds, teamCodes };
  }
  return null;
};

export const recapMovementLine = (settings) => {
  const line = movementSummary(settings);
  if (!line || line === "Stay on the same pair") return "";
  return line;
};

export const laneForBowler = ({
  pairStart,
  startingLane,
  bowlerIndex,
  bowlerQuantity,
  doublesPlacement,
}) => {
  const left = pairStart;
  const right = pairStart + 1;
  const qty = Number(bowlerQuantity) || 1;
  if (qty === 2 && (doublesPlacement || DOUBLES_SPLIT) === DOUBLES_SPLIT) {
    return bowlerIndex === 0 ? left : right;
  }
  const assigned = physicalLane(startingLane);
  if (assigned === left || assigned === right) return assigned;
  return left;
};

export const handicapForAverage = (average, hasAverage, tournament) => {
  if (hasAverage === false) return 0;
  const avg = Number(average);
  const basis = Number(tournament?.handicapBase);
  const percentage = Number(tournament?.handicapPercentage);
  if (
    !Number.isFinite(avg) ||
    !Number.isFinite(basis) ||
    !Number.isFinite(percentage)
  ) {
    return 0;
  }
  return calculateHandicap(
    avg,
    basis,
    percentage,
    tournament?.handicapMax,
    true
  );
};

const sidePotLabels = ({
  bowlerId,
  teamId,
  primaryEventId,
  eventEntries,
  eventsById,
}) => {
  const seen = new Set();
  const labels = [];
  (eventEntries || []).forEach((entry) => {
    if (entry?.bowlerID !== bowlerId || entry?.teamID !== teamId) return;
    if (entry?.eventsID === primaryEventId) return;
    const event = eventsById?.get?.(entry.eventsID) || eventsById?.[entry.eventsID];
    const label = optionalEventLabel(event || {});
    const key = String(label || "").toLowerCase();
    if (!label || seen.has(key)) return;
    seen.add(key);
    labels.push(label);
  });
  return labels.slice(0, 6);
};

const toBowlerRow = ({
  assignment,
  bowlerIndex,
  team,
  pairStart,
  settings,
  averagesByBowlerId,
  eventEntries,
  eventsById,
  tournament,
}) => {
  const bowlerId = assignment?.bowlerID || assignment?.bowlers?.id;
  const name = String(assignment?.bowlers?.name || assignment?.name || "").trim();
  const averageRow = averagesByBowlerId?.get?.(bowlerId) || averagesByBowlerId?.[bowlerId];
  const hasAverage = averageRow?.hasAverage !== false && averageRow?.average != null;
  const average = hasAverage ? Number(averageRow.average) : null;
  const encoding =
    settings?.encoding === ENCODING_LETTER_NUMBER
      ? encodingForBowlerIndex(
          Number(team.slot) || 1,
          bowlerIndex,
          settings,
          team.bowlerQuantity
        )
      : "";
  return {
    name: name || "Bowler",
    encoding,
    average: Number.isFinite(average) ? average : null,
    handicap: handicapForAverage(average, hasAverage, tournament),
    hasAverage,
    sidePots: sidePotLabels({
      bowlerId,
      teamId: team.id,
      primaryEventId: team.eventsID,
      eventEntries,
      eventsById,
    }),
    teamName: Number(team.bowlerQuantity) >= 3 ? String(team.teamName || "") : "",
    lane: laneForBowler({
      pairStart,
      startingLane: team.startingLane,
      bowlerIndex,
      bowlerQuantity: team.bowlerQuantity,
      doublesPlacement: settings?.doublesPlacement,
    }),
  };
};

export const buildRecapPairs = ({
  teams = [],
  settings = {},
  eventsById,
  averagesByBowlerId,
  eventEntries = [],
  tournament = {},
} = {}) => {
  const grouped = new Map();
  (teams || []).forEach((team) => {
    const pairStart = pairStartFromLane(team?.startingPair);
    const slot = Number(team?.slot);
    if (pairStart == null || slot < 1) return;
    const assignments = sortTeamAssignments(
      team.assignments || team.teamassignments || []
    );
    if (!assignments.length) return;
    const bowlers = assignments.map((assignment, bowlerIndex) =>
      toBowlerRow({
        assignment,
        bowlerIndex,
        team,
        pairStart,
        settings,
        averagesByBowlerId,
        eventEntries,
        eventsById,
        tournament,
      })
    );
    const games = Math.min(6, Math.max(1, Number(team.gamesPerEvent) || 3));
    const left = [];
    const right = [];
    bowlers.forEach((bowler) => {
      if (bowler.lane === pairStart + 1) right.push(bowler);
      else left.push(bowler);
    });
    const bowlerQuantity = Math.max(1, Number(team.bowlerQuantity) || 1);
    const showTeamName = bowlerQuantity >= 3 && Boolean(team.teamName);
    const shift = {
      slot,
      teamId: String(team.id || ""),
      teamName: showTeamName ? team.teamName : "",
      bowlerQuantity,
      games,
      left,
      right,
    };
    if (!grouped.has(pairStart)) grouped.set(pairStart, []);
    grouped.get(pairStart).push(shift);
  });

  return [...grouped.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([pairStart, shifts]) => ({
      pairStart,
      label: formatPair(pairStart),
      games: Math.max(...shifts.map((shift) => shift.games), 1),
      shifts: shifts.sort((a, b) => a.slot - b.slot),
    }));
};

export const recapContentHeight = () =>
  RECAP_LAYOUT.PAGE_H -
  RECAP_LAYOUT.MARGIN * 2 -
  RECAP_LAYOUT.TITLE_BAND -
  RECAP_LAYOUT.FOOTER;

export const recapGameCount = (games) =>
  Math.min(RECAP_LAYOUT.MAX_GAMES, Math.max(1, Number(games) || 3));

export const recapBlockHeight = (bowlers) =>
  RECAP_LAYOUT.TABLE_HEAD +
  RECAP_LAYOUT.ROW_H +
  Math.max((bowlers || []).length, 1) * RECAP_LAYOUT.ROW_H +
  RECAP_LAYOUT.TOTALS_H;

export const recapBlockRows = (pair) => {
  const spanning = [];
  const leftOnly = [];
  const rightOnly = [];
  (pair?.shifts || []).forEach((shift) => {
    const hasLeft = (shift?.left || []).length > 0;
    const hasRight = (shift?.right || []).length > 0;
    if (hasLeft && hasRight) spanning.push(shift);
    else if (hasLeft) leftOnly.push(shift);
    else if (hasRight) rightOnly.push(shift);
  });
  const rows = spanning.map((shift) => ({
    left: shift,
    right: shift,
    span: true,
  }));
  const n = Math.max(leftOnly.length, rightOnly.length);
  for (let i = 0; i < n; i += 1) {
    rows.push({
      left: leftOnly[i] || null,
      right: rightOnly[i] || null,
      span: false,
    });
  }
  if (!rows.length) {
    rows.push({ left: null, right: null, span: false });
  }
  return rows;
};

export const recapRowHeight = (row) => {
  const leftH = row?.left ? recapBlockHeight(row.left.left) : 0;
  const rightH = row?.right ? recapBlockHeight(row.right.right) : 0;
  return Math.max(leftH, rightH, leftH || rightH ? 0 : recapBlockHeight([]));
};

export const recapLaneRows = (pair) =>
  Math.max(
    1,
    ...(pair?.shifts || []).map((shift) =>
      Math.max((shift?.left || []).length, (shift?.right || []).length, 1)
    )
  );

export const recapPairHeight = (pair) => {
  const rows = recapBlockRows(pair);
  const tables = rows.reduce(
    (sum, row, index) =>
      sum + recapRowHeight(row) + (index > 0 ? RECAP_LAYOUT.SHIFT_GAP : 0),
    0
  );
  return RECAP_LAYOUT.PAIR_HEAD + tables;
};

const withFlags = (pair, { continued = false, continues = false } = {}) => ({
  ...pair,
  continued,
  continues,
});

const splitPairAcrossPages = (pair, available) => {
  const pages = [];
  let remaining = [...(pair.shifts || [])];
  let continued = false;
  while (remaining.length) {
    const taken = [];
    while (remaining.length) {
      const next = remaining[0];
      const candidate = withFlags(
        { ...pair, shifts: [...taken, next] },
        { continued, continues: remaining.length > 1 }
      );
      if (taken.length && recapPairHeight(candidate) > available) break;
      taken.push(remaining.shift());
    }
    if (!taken.length) {
      taken.push(remaining.shift());
    }
    pages.push(
      withFlags(
        { ...pair, shifts: taken },
        { continued, continues: remaining.length > 0 }
      )
    );
    continued = true;
  }
  return pages;
};

export const packRecapPages = (pairs = [], layout = LAYOUT_ONE) => {
  const available = recapContentHeight();
  const pages = [];
  if (layout !== LAYOUT_PACK) {
    (pairs || []).forEach((pair) => {
      splitPairAcrossPages(pair, available).forEach((pagePair) => {
        pages.push([pagePair]);
      });
    });
    return pages;
  }

  let current = [];
  let used = 0;
  (pairs || []).forEach((pair) => {
    const chunks = splitPairAcrossPages(pair, available);
    chunks.forEach((chunk) => {
      const height = recapPairHeight(chunk);
      const gap = current.length ? RECAP_LAYOUT.PAIR_GAP : 0;
      if (current.length && used + gap + height > available) {
        pages.push(current);
        current = [];
        used = 0;
      }
      current.push(chunk);
      used += (current.length > 1 ? RECAP_LAYOUT.PAIR_GAP : 0) + height;
    });
  });
  if (current.length) pages.push(current);
  return pages;
};

export const buildRecapSheetModel = ({
  tournamentId,
  tournamentName,
  eventName,
  squadId,
  squadLabel,
  bowlingCenterName,
  movementLine,
  layout,
  pairs,
} = {}) => ({
  tournamentId: tournamentId || "",
  tournamentName: tournamentName || "Tournament",
  eventName: eventName || "",
  squadId: squadId || "",
  squadLabel: squadLabel || "",
  bowlingCenterName: bowlingCenterName || "",
  movementLine: movementLine || "",
  layout: layout === LAYOUT_PACK ? LAYOUT_PACK : LAYOUT_ONE,
  pages: packRecapPages(pairs || [], layout === LAYOUT_PACK ? LAYOUT_PACK : LAYOUT_ONE),
});
